/**
 * MAP MOODS: every colour the map engine paints, for each time of day.
 * Owners: S4 (author), S5 (critic, amended 2026-09-24). Reasoning and the contrast
 * tables are in docs/design-spec.md §M; the critique is docs/design-critique.md.
 *
 *   day     "read on paper"  ivory land, the aesthetic's streets (lit fill, earth casing), sky water, sage parks
 *   golden  default          the aesthetic's golden-hour ramp; light on land carries a soil contact shade
 *   night   dusk glass       soil and night land, sand roads at low strength, ivory labels
 *
 * Consumers
 *   B2  flavor (@protomaps/basemaps), hillshade, contours, mood changes (1100 ms)
 *   B3  light (LightPalette) + cssVars --ld-route-shade, --ld-route-shade-px, --ld-glow-gain
 *   B5  places, overlays, cssVars for DOM pins, the veil and lit contours (spec §D.10)
 *
 * Rules
 *   - Every colour is a brand token, a stop of the aesthetic's TIMES ramps (aesthetic.jsx L555–560),
 *     or one of those mixed at a stated strength (mix(), rgba()). No free hex values: the three
 *     sun variants below are mixes of tokens too.
 *   - Sun is the only glow. Sun hues appear only in `light`, siteFill, the lit site outline,
 *     roadHighlight, lit contours, the tree-ring pulse and the selected pin.
 *   - Light on land carries its contact shade (--ld-route-shade) wherever a sun-coloured line
 *     would fall under 3:1 against the land (golden). Contours never glow and never take a shade.
 *   - The ridge is drawn in the contour ink, heavier: the land's own line, never mistaken for a road.
 *   - Group dots use tokens only: ink tokens on light land (day, golden) and lit tokens on dark
 *     land (night). Each is at least 3:1 against its mood's earth (ratios in the spec, §M.3).
 *   - Buildings stay very quiet: the layer is drawn at a fixed 0.5 opacity, so its colour sits
 *     6–12 % away from the land.
 *   - No `pois` key, so @protomaps/basemaps emits no POI layer (and no sprite requests).
 *
 * cssVars (all read by the engine from MOODS[m].cssVars and also set on the stage root)
 *   --ld-land, --ld-map-ink, --ld-map-halo, --ld-map-focus   DOM text and focus on the map
 *   --ld-vignette                                           edge scrim colour
 *   --ld-route-shade, --ld-route-shade-px                   contact shade under lit lines (B3, B5)
 *   --ld-glow-gain                                          multiplies every glow (B3, B5)
 *   --ld-pulse                                              tree-ring stroke (B5)
 *   --ld-pin-contact                                        1.5 px ring inside the selected (sun) pin
 *   --ld-veil                                               colour of the select veil at full strength (§D.10)
 *   --ld-contour-lit                                        lit contours around a destination (§D.10)
 */
import type { Flavor } from "@protomaps/basemaps";
import type { LightPalette, MoodSpec } from "@/contracts/map";
import type { Mood } from "@/contracts/store";
import { tokens } from "@/design/tokens";

const C = tokens.color;

/* ─── Colour helpers (pure, run once at module load) ─────────────────────── */

const toRgb = (hex: string): [number, number, number] => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, "$&$&") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const hx = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0").toUpperCase();

/** `fg` laid over `bg` at opacity `a` (0…1), returned as an opaque hex. */
export function mix(fg: string, a: number, bg: string): string {
  const f = toRgb(fg), b = toRgb(bg);
  return `#${hx(f[0] * a + b[0] * (1 - a))}${hx(f[1] * a + b[1] * (1 - a))}${hx(f[2] * a + b[2] * (1 - a))}`;
}

/** A token at an alpha, as an rgba() string (used where the colour must stay translucent). */
export function rgba(hex: string, a: number): string {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

/* ─── Aesthetic TIMES ramps (aesthetic.jsx L557–559), low → high ground ─── */

const TIMES = {
  day: { ramp: ["#C3BD9F", "#DFD2BA", "#EEE2CF"], water: "#89A9A2", corridor: "#8F9A73", lit: "#FFFAEE", shade: "#847668" },
  golden: { ramp: ["#A6946F", "#C9AA80", "#DDC096"], water: "#7E9690", corridor: "#848A5E", lit: "#FFD496", shade: "#604640" },
  night: { ramp: ["#1A140F", "#221A14", "#2C2219"], water: "#141C1C", corridor: "#1E2218", lit: "#4A4648", shade: "#0C0907" },
} as const;

/** Burnt sun: sun 55 % into earth, the sun's hue at paper-ink value (#AF7815, 3.09:1 on day land). Day only. */
const SUN_ON_PAPER = mix(C.sun, 0.55, C.earth);
/** The comet's hot centre: the day ramp's lit stop 70 % over sun-soft (#FCEBC2). */
const SUN_HOT = mix(TIMES.day.lit, 0.7, C.sunSoft);
/** Night glow: sun-soft 35 % into sun (#F3B423), a touch hotter so light reads brighter at dusk. */
const SUN_DUSK = mix(C.sunSoft, 0.35, C.sun);
/** The darkest ink the maps use: the night ramp's shade stop (#0C0907). Stands in for black. */
const INK_BLACK = TIMES.night.shade;

/** Self-hosted glyph fontstacks (M1 builds these into /glyphs/{fontstack}/{range}.pbf). */
export const FONTSTACKS = {
  regular: "Noto Sans Regular",
  bold: "Noto Sans Medium",
  italic: "Noto Sans Italic",
} as const;

/* ─── Flavor builder: a few roles → every Flavor key ─────────────────────── */

interface FlavorRoles {
  background: string;
  earth: string;
  landcover: NonNullable<Flavor["landcover"]>;
  park: [string, string]; //   [low zoom, high zoom]
  wood: [string, string];
  scrub: [string, string];
  hospital: string;
  school: string;
  industrial: string;
  pedestrian: string;
  sand: string;
  beach: string;
  glacier: string;
  zoo: string;
  military: string;
  aerodrome: string;
  runway: string;
  water: string;
  pier: string;
  buildings: string;
  boundaries: string;
  railway: string;
  road: { highway: string; major: string; minorA: string; minorB: string; service: string; other: string };
  casing: { highway: string; major: string; minor: string; service: string };
  tunnel: { major: string; minor: string; casing: string };
  bridgeCasing: { major: string; minor: string };
  label: {
    city: string; cityHalo: string;
    subplace: string; subplaceHalo: string;
    state: string; stateHalo: string;
    country: string;
    roadMajor: string; roadMajorHalo: string;
    roadMinor: string; roadMinorHalo: string;
    water: string;
    address: string; addressHalo: string;
  };
}

function buildFlavor(r: FlavorRoles): Flavor {
  return {
    background: r.background,
    earth: r.earth,
    park_a: r.park[0],
    park_b: r.park[1],
    hospital: r.hospital,
    industrial: r.industrial,
    school: r.school,
    wood_a: r.wood[0],
    wood_b: r.wood[1],
    pedestrian: r.pedestrian,
    scrub_a: r.scrub[0],
    scrub_b: r.scrub[1],
    glacier: r.glacier,
    sand: r.sand,
    beach: r.beach,
    aerodrome: r.aerodrome,
    runway: r.runway,
    water: r.water,
    zoo: r.zoo,
    military: r.military,

    tunnel_other_casing: r.tunnel.casing,
    tunnel_minor_casing: r.tunnel.casing,
    tunnel_link_casing: r.tunnel.casing,
    tunnel_major_casing: r.tunnel.casing,
    tunnel_highway_casing: r.tunnel.casing,
    tunnel_other: r.tunnel.minor,
    tunnel_minor: r.tunnel.minor,
    tunnel_link: r.tunnel.minor,
    tunnel_major: r.tunnel.major,
    tunnel_highway: r.tunnel.major,

    pier: r.pier,
    buildings: r.buildings,

    minor_service_casing: r.casing.service,
    minor_casing: r.casing.minor,
    link_casing: r.casing.major,
    major_casing_late: r.casing.major,
    highway_casing_late: r.casing.highway,
    other: r.road.other,
    minor_service: r.road.service,
    minor_a: r.road.minorA,
    minor_b: r.road.minorB,
    link: r.road.major,
    major_casing_early: r.casing.major,
    major: r.road.major,
    highway_casing_early: r.casing.highway,
    highway: r.road.highway,

    railway: r.railway,
    boundaries: r.boundaries,

    bridges_other_casing: r.bridgeCasing.minor,
    bridges_minor_casing: r.bridgeCasing.minor,
    bridges_link_casing: r.bridgeCasing.major,
    bridges_major_casing: r.bridgeCasing.major,
    bridges_highway_casing: r.bridgeCasing.major,
    bridges_other: r.road.other,
    bridges_minor: r.road.minorB,
    bridges_link: r.road.major,
    bridges_major: r.road.major,
    bridges_highway: r.road.highway,

    roads_label_minor: r.label.roadMinor,
    roads_label_minor_halo: r.label.roadMinorHalo,
    roads_label_major: r.label.roadMajor,
    roads_label_major_halo: r.label.roadMajorHalo,
    ocean_label: r.label.water,
    subplace_label: r.label.subplace,
    subplace_label_halo: r.label.subplaceHalo,
    city_label: r.label.city,
    city_label_halo: r.label.cityHalo,
    state_label: r.label.state,
    state_label_halo: r.label.stateHalo,
    country_label: r.label.country,
    address_label: r.label.address,
    address_label_halo: r.label.addressHalo,

    regular: FONTSTACKS.regular,
    bold: FONTSTACKS.bold,
    italic: FONTSTACKS.italic,
    landcover: r.landcover,
    // pois: deliberately omitted (no POI layer, no sprite).
  };
}

/* ═══ DAY — read on paper (12:40 pm) ═══════════════════════════════════════ */

const DAY_LAND = mix(C.ivory, 0.6, TIMES.day.ramp[2]); //       ≈ #F1E6D6, ivory laid over the day ramp's high stop

const DAY_FLAVOR = buildFlavor({
  background: mix(C.stone, 0.18, DAY_LAND),
  earth: DAY_LAND,
  landcover: {
    // visible only below z7 (the basemap fades landcover out 5→7)
    grassland: mix(C.sage, 0.1, DAY_LAND),
    farmland: mix(C.olive, 0.08, DAY_LAND),
    forest: mix(C.sage, 0.2, DAY_LAND),
    scrub: mix(C.sage, 0.13, DAY_LAND),
    barren: mix(C.sand, 0.14, DAY_LAND),
    urban_area: mix(C.stone, 0.4, DAY_LAND),
    glacier: C.ivory,
  },
  park: [mix(C.sage, 0.16, DAY_LAND), mix(C.sage, 0.26, DAY_LAND)],
  wood: [mix(C.sage, 0.22, DAY_LAND), mix(C.sage, 0.32, DAY_LAND)],
  scrub: [mix(C.sage, 0.12, DAY_LAND), mix(C.sage, 0.2, DAY_LAND)],
  hospital: mix(C.terracotta, 0.05, DAY_LAND),
  school: mix(C.sand, 0.12, DAY_LAND),
  industrial: mix(C.stone, 0.5, DAY_LAND),
  pedestrian: mix(C.sand, 0.1, DAY_LAND),
  sand: mix(C.sand, 0.25, DAY_LAND),
  beach: mix(C.sand, 0.3, DAY_LAND),
  glacier: C.ivory,
  zoo: mix(C.sage, 0.16, DAY_LAND),
  military: mix(C.stone, 0.4, DAY_LAND),
  aerodrome: mix(C.stone, 0.55, DAY_LAND),
  runway: C.stone,
  water: mix(C.sky, 0.8, DAY_LAND), //  sky, lifted toward the paper so water labels hold 4.8:1
  pier: mix(C.stone, 0.6, DAY_LAND),
  buildings: mix(C.stone, 0.75, DAY_LAND), //  × 0.5 fill-opacity ≈ 5 % off the land
  boundaries: mix(C.mud, 0.35, DAY_LAND),
  railway: mix(C.earth, 0.55, DAY_LAND),
  // The aesthetic's streets (.ld-street / .ld-street-case): a lit fill with a thin earth casing.
  // Roads recede into the paper, so the burnt-sun route is the strongest line on the map (S5).
  road: {
    highway: TIMES.day.lit,
    major: mix(TIMES.day.lit, 0.85, DAY_LAND),
    minorA: mix(TIMES.day.lit, 0.6, DAY_LAND),
    minorB: mix(TIMES.day.lit, 0.7, DAY_LAND),
    service: mix(TIMES.day.lit, 0.45, DAY_LAND),
    other: mix(C.earth, 0.1, DAY_LAND),
  },
  casing: {
    highway: mix(C.earth, 0.38, DAY_LAND),
    major: mix(C.earth, 0.3, DAY_LAND),
    minor: mix(C.earth, 0.16, DAY_LAND),
    service: mix(C.earth, 0.12, DAY_LAND),
  },
  tunnel: { major: mix(TIMES.day.lit, 0.6, DAY_LAND), minor: mix(TIMES.day.lit, 0.4, DAY_LAND), casing: mix(C.earth, 0.2, DAY_LAND) },
  bridgeCasing: { major: mix(C.earth, 0.45, DAY_LAND), minor: mix(C.earth, 0.3, DAY_LAND) },
  label: {
    city: C.earth, cityHalo: C.ivory,
    subplace: C.mud, subplaceHalo: DAY_LAND,
    state: mix(C.mud, 0.6, DAY_LAND), stateHalo: DAY_LAND,
    country: mix(C.mud, 0.6, DAY_LAND),
    roadMajor: C.mud, roadMajorHalo: DAY_LAND,
    roadMinor: mix(C.mud, 0.8, DAY_LAND), roadMinorHalo: DAY_LAND,
    water: C.forest,
    address: C.mud, addressHalo: DAY_LAND,
  },
});

const DAY_LIGHT: LightPalette = {
  core: SUN_ON_PAPER, //   burnt sun, 3.09:1 on paper (3.31 under the veil)
  trail: C.ochre, //       fades to the brand mark's ochre
  glow: C.sun,
  comet: C.sun,
  halo: C.sunSoft,
  bloom: C.ochre,
  preview: SUN_ON_PAPER, // hover = light arriving: the lit colour, drawn at alpha 0.5 by B3
};

/* ═══ GOLDEN — golden hour (6:05 pm, default) ══════════════════════════════ */

const GOLDEN_LAND = mix(TIMES.golden.ramp[2], 0.25, TIMES.golden.ramp[1]); // ≈ #CEB086, mid stop nudged 25 % toward high ground

const GOLDEN_FLAVOR = buildFlavor({
  background: mix(TIMES.golden.ramp[0], 0.3, GOLDEN_LAND),
  earth: GOLDEN_LAND,
  landcover: {
    grassland: mix(C.olive, 0.12, GOLDEN_LAND),
    farmland: mix(TIMES.golden.ramp[2], 0.5, GOLDEN_LAND),
    forest: mix(TIMES.golden.ramp[0], 0.7, GOLDEN_LAND),
    scrub: mix(C.olive, 0.18, GOLDEN_LAND),
    barren: TIMES.golden.ramp[2],
    urban_area: mix(C.earth, 0.06, GOLDEN_LAND),
    glacier: TIMES.golden.ramp[2],
  },
  // Olive parks tinted with sage-lit so they turn green without darkening: ink dots keep 3:1 on them.
  park: [mix(C.sageLit, 0.3, GOLDEN_LAND), mix(C.sageLit, 0.45, GOLDEN_LAND)],
  wood: [mix(C.sageLit, 0.45, GOLDEN_LAND), mix(C.sageLit, 0.6, GOLDEN_LAND)],
  scrub: [mix(C.sageLit, 0.2, GOLDEN_LAND), mix(C.sageLit, 0.3, GOLDEN_LAND)],
  // Landuse pads lift toward ivory/stone (never darker than the land) so the dots on them keep 3:1.
  hospital: mix(C.ivory, 0.1, GOLDEN_LAND),
  school: mix(C.sand, 0.15, GOLDEN_LAND),
  industrial: mix(C.stone, 0.3, GOLDEN_LAND),
  pedestrian: mix(C.ivory, 0.12, GOLDEN_LAND),
  sand: mix(C.sand, 0.35, GOLDEN_LAND),
  beach: mix(C.sand, 0.4, GOLDEN_LAND),
  glacier: TIMES.golden.ramp[2],
  zoo: mix(C.sageLit, 0.3, GOLDEN_LAND),
  military: mix(C.stone, 0.2, GOLDEN_LAND),
  aerodrome: mix(C.stone, 0.25, GOLDEN_LAND),
  runway: mix(C.ivory, 0.35, GOLDEN_LAND),
  water: TIMES.golden.water,
  pier: mix(C.ivory, 0.25, GOLDEN_LAND),
  buildings: mix(C.earth, 0.14, GOLDEN_LAND), // × 0.5 fill-opacity ≈ 7 % off the land
  boundaries: mix(C.earth, 0.35, GOLDEN_LAND),
  railway: mix(C.earth, 0.45, GOLDEN_LAND),
  road: {
    // soft ivory hairlines (the aesthetic's --street: ivory at .62 over the land)
    highway: mix(C.ivory, 0.7, GOLDEN_LAND),
    major: mix(C.ivory, 0.58, GOLDEN_LAND),
    minorA: mix(C.ivory, 0.3, GOLDEN_LAND),
    minorB: mix(C.ivory, 0.4, GOLDEN_LAND),
    service: mix(C.ivory, 0.25, GOLDEN_LAND),
    other: mix(C.ivory, 0.22, GOLDEN_LAND),
  },
  casing: {
    // --street-case: earth at .25
    highway: mix(C.earth, 0.3, GOLDEN_LAND),
    major: mix(C.earth, 0.22, GOLDEN_LAND),
    minor: mix(C.earth, 0.14, GOLDEN_LAND),
    service: mix(C.earth, 0.1, GOLDEN_LAND),
  },
  tunnel: { major: mix(C.ivory, 0.35, GOLDEN_LAND), minor: mix(C.ivory, 0.2, GOLDEN_LAND), casing: mix(C.earth, 0.12, GOLDEN_LAND) },
  bridgeCasing: { major: mix(C.earth, 0.38, GOLDEN_LAND), minor: mix(C.earth, 0.25, GOLDEN_LAND) },
  label: {
    city: C.earth, cityHalo: TIMES.golden.ramp[2],
    subplace: C.earth, subplaceHalo: TIMES.golden.ramp[2],
    state: mix(C.earth, 0.55, GOLDEN_LAND), stateHalo: TIMES.golden.ramp[2],
    country: mix(C.earth, 0.55, GOLDEN_LAND),
    roadMajor: C.earth, roadMajorHalo: TIMES.golden.ramp[2],
    roadMinor: mix(C.earth, 0.85, GOLDEN_LAND), roadMinorHalo: TIMES.golden.ramp[2],
    // Forest deepened into night (#27281C): 4.73:1 on golden water, 7.24:1 on the land (S5; forest was 3.21).
    water: mix(C.forest, 0.45, C.night),
    address: mix(C.earth, 0.7, GOLDEN_LAND), addressHalo: TIMES.golden.ramp[2],
  },
});

const GOLDEN_LIGHT: LightPalette = {
  core: C.sunSoft, //  lit line; sits on --ld-route-shade for its 3:1 edge
  trail: C.sand,
  glow: C.sun,
  comet: SUN_HOT,
  halo: C.sun,
  bloom: C.sunSoft,
  preview: C.sunSoft, // with its shade, both at alpha 0.5 (ivory at .45 measured 1.2:1, unseen)
};

/* ═══ NIGHT — dusk glass (9:30 pm) ═════════════════════════════════════════ */

const NIGHT_LAND = mix(TIMES.night.ramp[1], 0.5, TIMES.night.ramp[0]); //   ≈ #1E1712

const NIGHT_FLAVOR = buildFlavor({
  background: C.night,
  earth: NIGHT_LAND,
  landcover: {
    grassland: mix(C.sage, 0.06, NIGHT_LAND),
    farmland: mix(C.olive, 0.05, NIGHT_LAND),
    forest: mix(TIMES.night.corridor, 0.8, NIGHT_LAND),
    scrub: mix(C.sage, 0.07, NIGHT_LAND),
    barren: mix(C.sand, 0.05, NIGHT_LAND),
    urban_area: mix(C.sand, 0.06, NIGHT_LAND),
    glacier: TIMES.night.ramp[2],
  },
  park: [mix(C.sage, 0.08, NIGHT_LAND), mix(TIMES.night.corridor, 0.9, NIGHT_LAND)],
  wood: [mix(C.forest, 0.3, NIGHT_LAND), mix(C.forest, 0.45, NIGHT_LAND)],
  scrub: [mix(C.sage, 0.06, NIGHT_LAND), mix(C.sage, 0.1, NIGHT_LAND)],
  hospital: mix(C.terracotta, 0.05, NIGHT_LAND),
  school: mix(C.sand, 0.05, NIGHT_LAND),
  industrial: mix(C.stone, 0.05, NIGHT_LAND),
  pedestrian: mix(C.sand, 0.06, NIGHT_LAND),
  sand: mix(C.sand, 0.08, NIGHT_LAND),
  beach: mix(C.sand, 0.1, NIGHT_LAND),
  glacier: TIMES.night.ramp[2],
  zoo: mix(C.sage, 0.08, NIGHT_LAND),
  military: mix(C.stone, 0.04, NIGHT_LAND),
  aerodrome: mix(C.stone, 0.06, NIGHT_LAND),
  runway: mix(C.sand, 0.14, NIGHT_LAND),
  water: TIMES.night.water,
  pier: mix(C.sand, 0.1, NIGHT_LAND),
  buildings: mix(C.sand, 0.12, NIGHT_LAND), // × 0.5 fill-opacity ≈ 6 % off the land
  boundaries: mix(C.sand, 0.18, NIGHT_LAND),
  railway: mix(C.sand, 0.22, NIGHT_LAND),
  road: {
    // sand at low strength
    highway: mix(C.sand, 0.34, NIGHT_LAND),
    major: mix(C.sand, 0.26, NIGHT_LAND),
    minorA: mix(C.sand, 0.12, NIGHT_LAND),
    minorB: mix(C.sand, 0.16, NIGHT_LAND),
    service: mix(C.sand, 0.1, NIGHT_LAND),
    other: mix(C.sand, 0.08, NIGHT_LAND),
  },
  casing: {
    // --street-case at night: black at .30 (the night ramp's shade stop stands in for black)
    highway: mix(INK_BLACK, 0.3, NIGHT_LAND),
    major: mix(INK_BLACK, 0.3, NIGHT_LAND),
    minor: mix(INK_BLACK, 0.2, NIGHT_LAND),
    service: mix(INK_BLACK, 0.15, NIGHT_LAND),
  },
  tunnel: { major: mix(C.sand, 0.14, NIGHT_LAND), minor: mix(C.sand, 0.07, NIGHT_LAND), casing: mix(INK_BLACK, 0.15, NIGHT_LAND) },
  bridgeCasing: { major: mix(INK_BLACK, 0.45, NIGHT_LAND), minor: mix(INK_BLACK, 0.3, NIGHT_LAND) },
  label: {
    city: rgba(C.ivory, 0.6), cityHalo: rgba(C.night, 0.85),
    subplace: rgba(C.ivory, 0.5), subplaceHalo: rgba(C.night, 0.85),
    state: rgba(C.ivory, 0.38), stateHalo: rgba(C.night, 0.85),
    country: rgba(C.ivory, 0.38),
    roadMajor: rgba(C.sand, 0.65), roadMajorHalo: rgba(C.night, 0.85),
    roadMinor: rgba(C.sand, 0.55), roadMinorHalo: rgba(C.night, 0.85),
    water: rgba(C.skyLit, 0.7),
    address: rgba(C.sand, 0.45), addressHalo: rgba(C.night, 0.85),
  },
});

const NIGHT_LIGHT: LightPalette = {
  core: C.sunSoft,
  trail: C.ochre,
  glow: SUN_DUSK, //   brighter glow at dusk
  comet: SUN_HOT,
  halo: C.sun,
  bloom: C.sun,
  preview: C.sunSoft, // drawn at alpha 0.5 by B3
};

/* ═══ The three moods ══════════════════════════════════════════════════════ */

export const MOODS: Record<Mood, MoodSpec> = {
  day: {
    key: "day",
    label: "Day",
    clock: "12:40 pm",
    flavor: DAY_FLAVOR,
    light: DAY_LIGHT,
    contours: {
      minor: rgba(C.earth, 0.16),
      major: rgba(C.earth, 0.3),
      label: rgba(C.earth, 0.8), //  4.89:1 on its halo (was .62 → 3.18)
      halo: rgba(C.ivory, 0.85),
      ridge: rgba(C.earth, 0.5), //  the index line in contour ink (ochre read as a route by day)
      minorWidth: 0.8,
      majorWidth: 1.2,
    },
    hillshade: {
      shadow: rgba(TIMES.day.shade, 0.3),
      highlight: rgba(TIMES.day.lit, 0.4),
      accent: rgba(C.earth, 0.1),
      exaggeration: 0.3,
      illuminationDirection: 315, // cartographic north-west light; the noon sun (160°) would invert the relief
    },
    overlays: {
      ring: rgba(C.earth, 0.48),
      ringLabel: C.earth,
      ringLabelHalo: rgba(C.ivory, 0.92),
      siteFill: C.sun,
      siteFillOpacity: 0.1,
      siteLine: SUN_ON_PAPER,
      road: mix(C.earth, 0.8, DAY_LAND),
      roadCase: C.sand,
      roadHighlight: SUN_ON_PAPER,
      future: C.forest,
      futureLabel: C.forest,
      futureLabelHalo: rgba(C.ivory, 0.92),
    },
    places: {
      dot: { A: C.charcoal, B: C.terracottaInk, C: C.sageInk, D: C.earth },
      dotStroke: C.ivory,
      label: C.earth,
      labelHalo: rgba(C.ivory, 0.95),
      cluster: C.earth,
      clusterStroke: rgba(C.earth, 0.4),
      clusterText: C.ivory,
    },
    cssVars: {
      "--ld-land": DAY_LAND,
      "--ld-map-ink": C.earth,
      "--ld-map-halo": rgba(C.ivory, 0.9),
      "--ld-map-focus": C.earth,
      "--ld-vignette": rgba(C.earth, 0.16),
      // No shade by day: burnt sun already holds 3.09:1 on paper, and earth .18 under it
      // measured 2.31:1 core-on-shade, so the shade only muddied the line (S5).
      "--ld-route-shade": rgba(C.earth, 0),
      "--ld-route-shade-px": "0",
      "--ld-glow-gain": "0.45",
      "--ld-pulse": C.ochre,
      "--ld-pin-contact": rgba(C.earth, 0.35),
      "--ld-veil": rgba(TIMES.day.lit, 0.4), //   by day the map recedes into the paper; the route gains (3.09 → 3.31)
      "--ld-contour-lit": SUN_ON_PAPER,
    },
  },

  golden: {
    key: "golden",
    label: "Golden hour",
    clock: "6:05 pm",
    flavor: GOLDEN_FLAVOR,
    light: GOLDEN_LIGHT,
    contours: {
      // .ld-stage golden values (aesthetic L1408)
      minor: rgba(C.earth, 0.2),
      major: rgba(C.earth, 0.38),
      label: rgba(C.soil, 0.72), //  4.69:1 on its halo (earth .72 measured 3.19)
      halo: rgba(TIMES.golden.ramp[2], 0.8),
      ridge: rgba(C.earth, 0.6), //  contour ink, heavier; ivory read as a road beside the ivory streets
      minorWidth: 0.8,
      majorWidth: 1.25,
    },
    hillshade: {
      shadow: rgba(TIMES.golden.shade, 0.45),
      highlight: rgba(TIMES.golden.lit, 0.35),
      accent: rgba(TIMES.golden.shade, 0.14),
      exaggeration: 0.45,
      illuminationDirection: 285, // the golden-hour sun azimuth from TIMES
    },
    overlays: {
      ring: rgba(C.ivory, 0.8), //   .ld-walkring
      ringLabel: C.ivory,
      ringLabelHalo: rgba(C.soil, 0.6), // .ld-walkring-l stroke
      siteFill: C.sun,
      siteFillOpacity: 0.12,
      siteLine: rgba(C.soil, 0.7), // the outline at rest, 4.14:1 (sun-soft measured 1.30); lit by the LightLayer
      road: C.ivory,
      roadCase: rgba(C.earth, 0.4),
      roadHighlight: C.sunSoft,
      future: C.forest,
      futureLabel: C.forest,
      futureLabelHalo: rgba(TIMES.golden.ramp[2], 0.9),
    },
    places: {
      dot: { A: C.charcoal, B: C.terracottaInk, C: C.sageInk, D: C.earth },
      dotStroke: rgba(C.ivory, 0.9),
      label: C.soil,
      labelHalo: rgba(TIMES.golden.ramp[2], 0.9),
      cluster: rgba(C.soil, 0.86),
      clusterStroke: rgba(C.soil, 0.45),
      clusterText: C.ivory,
    },
    cssVars: {
      "--ld-land": GOLDEN_LAND,
      "--ld-map-ink": C.soil,
      "--ld-map-halo": rgba(TIMES.golden.ramp[2], 0.85),
      "--ld-map-focus": C.soil,
      "--ld-vignette": rgba(C.night, 0.42),
      // Soil .66: 3.77:1 edge on the land and 3.35 under the veil; the core is 5.38:1 on it (S5; was .60).
      "--ld-route-shade": rgba(C.soil, 0.66),
      "--ld-route-shade-px": "4",
      "--ld-glow-gain": "0.8",
      "--ld-pulse": C.sunSoft, //   sun measured 1.03:1 on golden land; rings also take the contact shade
      "--ld-pin-contact": rgba(C.soil, 0.6),
      "--ld-veil": rgba(C.soil, 0.1), //   the land dims a little so the light carries (.12 read muddy in the render)
      "--ld-contour-lit": C.sun,
    },
  },

  night: {
    key: "night",
    label: "Night",
    clock: "9:30 pm",
    flavor: NIGHT_FLAVOR,
    light: NIGHT_LIGHT,
    contours: {
      // .ld-stage[data-time="night"] (aesthetic L1409)
      minor: rgba(C.sand, 0.11),
      major: rgba(C.sand, 0.24),
      label: rgba(C.sand, 0.65), //  4.92:1 on its halo (was .55 → 3.85)
      halo: rgba(C.night, 0.9),
      ridge: rgba(C.sand, 0.45),
      minorWidth: 0.8,
      majorWidth: 1.25,
    },
    hillshade: {
      shadow: rgba(TIMES.night.shade, 0.6),
      highlight: rgba(TIMES.night.lit, 0.18),
      accent: rgba(TIMES.night.shade, 0.2),
      exaggeration: 0.3,
      illuminationDirection: 300, // low moonlight from the north-west; the TIMES azimuth (230°) would invert the relief
    },
    overlays: {
      ring: rgba(C.sand, 0.4),
      ringLabel: rgba(C.ivory, 0.75),
      ringLabelHalo: rgba(C.night, 0.9),
      siteFill: C.sun,
      siteFillOpacity: 0.1,
      siteLine: C.sunSoft,
      road: rgba(C.sand, 0.55),
      roadCase: rgba(INK_BLACK, 0.35),
      roadHighlight: C.sunSoft,
      future: C.skyLit,
      futureLabel: C.skyLit,
      futureLabelHalo: rgba(C.night, 0.9),
    },
    places: {
      dot: { A: C.stone, B: C.terracottaLit, C: C.sageLit, D: C.sand },
      dotStroke: C.night,
      label: rgba(C.ivory, 0.86),
      labelHalo: rgba(C.night, 0.9),
      cluster: rgba(C.earth, 0.92),
      clusterStroke: rgba(C.sand, 0.45),
      clusterText: C.ivory,
    },
    cssVars: {
      "--ld-land": NIGHT_LAND,
      "--ld-map-ink": C.ivory,
      "--ld-map-halo": rgba(C.night, 0.8),
      "--ld-map-focus": C.sunSoft,
      "--ld-vignette": rgba(C.night, 0.6),
      "--ld-route-shade": rgba(C.night, 0),
      "--ld-route-shade-px": "0",
      "--ld-glow-gain": "1",
      "--ld-pulse": C.sun,
      "--ld-pin-contact": rgba(C.night, 0),
      "--ld-veil": rgba(C.night, 0.45),
      "--ld-contour-lit": C.sunSoft,
    },
  },
};

/** LightIsland order, icons (IconName) and the sun-arc angle from TIMES. */
export const MOOD_ORDER: readonly Mood[] = ["day", "golden", "night"];
export const MOOD_ICON: Record<Mood, "sun" | "sunset" | "moon"> = { day: "sun", golden: "sunset", night: "moon" };
export const MOOD_ARC: Record<Mood, number> = { day: 90, golden: 160, night: 205 };
/** Paint transition for a mood change (ms). MapLibre eases style transitions with its own
 *  cubic in-out, which stands in for drift; hillshade direction is tweened by B2 (spec §E.8). */
export const MOOD_TRANSITION_MS = 1100;
/** The intro's sunrise (night → golden), spec §E.7. */
export const MOOD_SUNRISE_MS = 2400;
